<template>
  <teleport to="body">
    <div v-if="visible === true" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-base-100 dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-lg mx-4" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <h3 class="text-lg font-semibold mb-1">{{ t('agentCreate.knowledge.filesModal.title', 'Upload documents') }}</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">{{ t('agentCreate.knowledge.filesModal.hint', 'Upload PDF, Word, text, and similar files.') }}</p>
        <div
          class="w-full p-6 border-2 border-dashed rounded-xl cursor-pointer border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700"
          @click="triggerFileInput"
        >
          <div class="flex flex-col items-center text-center">
            <Icon icon="tabler:file-upload" width="32" class="text-blue-500 dark:text-blue-300 mb-2" />
            <span class="text-sm text-gray-600 dark:text-gray-400">{{ t('agentCreate.knowledge.filesModal.dropHint', 'Click to select files') }}</span>
          </div>
          <input ref="fileInputRef" type="file" class="hidden" multiple accept=".pdf,.doc,.docx,.txt,.xlsx" @change="onFileChange" />
        </div>
        <div v-if="selectedFiles.length > 0" class="mt-4 max-h-40 overflow-y-auto">
          <div
            v-for="fileItem in selectedFiles"
            :key="fileItem.id"
            class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700"
          >
            <span class="text-sm truncate flex-1 min-w-0" :class="isRtl === true ? 'ml-2' : 'mr-2'">{{ fileItem.name }}</span>
            <button
              type="button"
              class="inline-flex items-center justify-center w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 text-slate-400 transition-colors hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0"
              :title="t('common.remove', 'Remove')"
              @click="removeFile(fileItem.id)"
            >
              <Icon icon="tabler:trash" width="16" />
            </button>
          </div>
        </div>
        <div class="flex justify-end mt-6">
          <button type="button" class="btn" @click="onClose">{{ t('common.cancel', 'Cancel') }}</button>
          <button type="button" class="btn ml-2" :disabled="canConfirm === false" @click="onConfirm">{{ t('common.confirm', 'Confirm') }}</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'confirm']);

const { t, currentLanguage, availableLanguages } = useI18n();
const fileInputRef = ref(null);
const selectedFiles = ref([]);
let fileCounter = 0;

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const canConfirm = computed(() => {
  return selectedFiles.value.length > 0;
});

watch(() => props.visible, (value) => {
  if (value === true) {
    selectedFiles.value = [];
    fileCounter = 0;
  }
});

function triggerFileInput() {
  if (fileInputRef.value !== null && fileInputRef.value !== undefined) {
    fileInputRef.value.click();
  }
}

function onFileChange(event) {
  const target = event.target;
  if (target === null || target === undefined || target.files === null) {
    return;
  }
  let index = 0;
  while (index < target.files.length) {
    const file = target.files[index];
    fileCounter = fileCounter + 1;
    selectedFiles.value = selectedFiles.value.concat([{
      id: `file-${fileCounter}`,
      name: file.name,
      file
    }]);
    index = index + 1;
  }
  target.value = '';
}

function removeFile(id) {
  selectedFiles.value = selectedFiles.value.filter((item) => item.id !== id);
}

function onClose() {
  emit('close');
}

function onConfirm() {
  if (canConfirm.value === false) {
    return;
  }
  const files = [];
  let index = 0;
  while (index < selectedFiles.value.length) {
    files.push(selectedFiles.value[index].file);
    index = index + 1;
  }
  emit('confirm', files);
}
</script>
